import React from "react"
import { shallowEqual } from "react-redux"
import { registerEventLog } from "../components/editor/event-logs/event-log-store"
import { store, useSelector } from "../store"
import { formatTimestamp } from "../utils/format-timestamp"
import { useRequiredContext } from "../utils/use-required-context"

// TODO: RECURSIVE NAMESPACES

export const NAMESPACE_SYMBOL: unique symbol = Symbol('NAMESPACE')
export const GLOBAL_NAMESPACE: unique symbol = Symbol('GLOBAL_NAMESPACE')
export type ComponentNamespace = string | typeof GLOBAL_NAMESPACE

export type PublishedDict = FlatPublishedDict & Namespaces
type FlatPublishedDict = Record<WistorVarName, WistorVarValue>
type Namespaces = { [NAMESPACE_SYMBOL]?: Record<string, PublishedDict> }
export type WistorVarName = string
export type WistorVarValue = unknown

export type PublishToNamespace = (key: WistorVarName, value: WistorVarValue, eventCtx: string) => void

export type PublishAction
  = { type: 'PUBLISH', data: {update: PublishedDict, eventCtx: string | undefined} }
  | { type: 'PUBLISH2', data: any }
  | { type: 'RESET' }

export type PublishType = 'PUBLISH' | 'PUBLISH2'

declare global {
  interface Window {
    changes: any
  }
}

export default function handlePubSub(
  state: PublishedDict = {},
  action: PublishAction,
): PublishedDict {
  switch (action.type) {

    case 'PUBLISH': {
      if (alreadyPublished(state, action.data.update)) return state

      const timestamp = new Date()
      const timestampStr = String(timestamp.getTime())
      const timestampEventLog = formatTimestamp(timestamp)
      const newState = mergeVars(
        state,
        action.data.update,
        timestampStr,
        (key, oldValue, newValue, namespace) => registerEventLog({
          type: 'app-variable-update-event',
          timestamp: timestampEventLog,
          namespace: namespace ?? GLOBAL_NAMESPACE,
          key,
          valueOld: (oldValue as string),
          valueNew: (newValue as string),
          ctx: action.data.eventCtx,
        })
      )

      if (window.top != null) window.top.changes = newState; // used by popup button. I would like to make this more generic

      return newState
    }

    case 'PUBLISH2': {
      console.log('publish 2', state, action.data)
      return state
    }

    case 'RESET': {
      return {}
    }

    default: {
      return state
    }

  }
}

/**
 * True when every value in `update` is already the value held in `state`.
 *
 * Republishing an identical value used to produce a new state object (and a fresh
 * `<key>Timestamp`), which re-rendered every subscriber, which could publish again.
 * Keeping the state as it is leaves those cycles nothing to feed on.
 */
function alreadyPublished(state: PublishedDict, update: PublishedDict): boolean {
  for (const key in update) {
    if (state[key] !== update[key]) return false
  }

  for (const namespace of getNamespaces(update)) {
    const stateScoped = getScopedVars(state, namespace)
    if (!stateScoped) return false
    if (!alreadyPublished(stateScoped, getScopedVars(update, namespace)!)) return false
  }

  return true
}

export function mergeVars(
  a: PublishedDict,
  b: PublishedDict,
  timestamp?: string,
  registerChange?: (key: string, oldValue: WistorVarValue, newValue: WistorVarValue, namespace: null | undefined | string) => void,
): PublishedDict {
  const result = {...a}

  for (const key in b) {
    const oldValue = a[key]
    const newValue = b[key]
    result[key] = newValue
    if (timestamp) result[key+"Timestamp"] = timestamp
    registerChange?.(key, oldValue, newValue, null)
  }

  for (const namespace of getNamespaces(b)) {
    const aScoped = getScopedVars(a, namespace) ?? {}
    const bScoped = getScopedVars(b, namespace) ?? {}

    const registerScopedChange = (key: string, oldValue: WistorVarValue, newValue: WistorVarValue) =>
      registerChange?.(key, oldValue, newValue, namespace)

    const mergedVars = mergeVars(aScoped, bScoped, timestamp, registerScopedChange)
    mutateSetScopedVars(result, mergedVars, namespace)
  }

  return result
}

export const ComponentNamespaceContext = React.createContext<null | ComponentNamespace>(null)

export function useNamespace() {
  return useRequiredContext(ComponentNamespaceContext)
}

export function usePubsub(namespaceOverride?: ComponentNamespace): [PublishedDict, PublishToNamespace] {
  const currentNamespace = useNamespace()
  const namespace = namespaceOverride ?? currentNamespace

  const pubsub = useSelector(s => s.rootAppVars_INTERNAL ?? ({} as PublishedDict))
  const namespacedVars = namespace === GLOBAL_NAMESPACE
      ? pubsub
      : getScopedVars(pubsub, namespace) ?? {}

  const publishToNamespace = React.useCallback(function publishToNamespace(topic: string, value: unknown, eventCtx: string) {
    publish(topic, value, namespace, eventCtx)
  }, [ namespace ])

  return [ namespacedVars, publishToNamespace ] as const
}

/**
 * Like usePubsub, but picks only `topics` and re-renders only when one of them changes.
 * For components rendered many times over (e.g. per row) that read a handful of variables.
 */
export function usePubsubTopics(topics: readonly string[]): PublishedDict {
  const namespace = useNamespace()
  return useSelector(s => {
    const root = s.rootAppVars_INTERNAL ?? ({} as PublishedDict)
    const scoped = namespace === GLOBAL_NAMESPACE ? root : getScopedVars(root, namespace) ?? {}
    const picked: PublishedDict = {}
    for (const topic of topics) picked[topic] = scoped[topic]
    return picked
  }, shallowEqual)
}

export function getPubsub(namespace: ComponentNamespace) {
  const pubsub = store.getState()?.rootAppVars_INTERNAL ?? ({} as PublishedDict)
  return namespace === GLOBAL_NAMESPACE
    ? pubsub
    : getScopedVars(pubsub, namespace)
}

/**
 * @param namespace also `undefined` for compatibility, since not everything is checked by typescript
 * @param eventCtx also `undefined` for compatibility, since not everything is checked by typescript
 */
export function publish(
  topic: WistorVarName,
  value: WistorVarValue,
  namespace: ComponentNamespace | undefined,
  eventCtx: string | undefined,
) {
  const update = namespace == null || namespace === GLOBAL_NAMESPACE
    ? {[topic]: value}
    : makeNamespacedUpdate(namespace, {[topic]: value})
  store.dispatch<PublishAction>({type: "PUBLISH", data: {update, eventCtx}})
}

/**
 * @param namespace also `undefined` for compatibility, since not everything is checked by typescript
 * @param eventCtx also `undefined` for compatibility, since not everything is checked by typescript
 */
export function publishDict(
  vars: FlatPublishedDict,
  namespace: ComponentNamespace | undefined,
  eventCtx: string | undefined,
) {
  const update = namespace == null || namespace === GLOBAL_NAMESPACE
    ? vars
    : makeNamespacedUpdate(namespace, vars)
  store.dispatch<PublishAction>({type: "PUBLISH", data: {update, eventCtx}})
}

function makeNamespacedUpdate(namespace: string, update: FlatPublishedDict): PublishedDict {
  const namespacedUpdate = {}
  mutateEnsureNamespaced(namespacedUpdate, namespace)
  const namespacedData = getScopedVars(namespacedUpdate, namespace)! // `!` since `mutateEnsureNamespaced(data, namespace)`
  for (const key in update) {
    namespacedData[key] = update[key]
  }
  return namespacedUpdate
}

export function publishWith(namespace: ComponentNamespace, eventCtx: string) {
  return function publishToNamespace(key: WistorVarName, value: WistorVarValue) {
    publish(key, value, namespace, eventCtx)
  }
}

export function resetAppVars() {
  store.dispatch<PublishAction>({type: 'RESET'})
}

export function getNamespaces(scopedState: PublishedDict): string[] {
  const namespaces = scopedState[NAMESPACE_SYMBOL]
  return namespaces ? Object.keys(namespaces) : []
}

export function getScopedVars(scopedState: PublishedDict, namespace: string): PublishedDict | null {
  const namespaces = scopedState[NAMESPACE_SYMBOL]
  if (!namespaces) return null
  else return namespaces[namespace] ?? null
}

function mutateSetScopedVars(state: PublishedDict, scopedVars: PublishedDict, namespace: string) {
  if (!state[NAMESPACE_SYMBOL]) state[NAMESPACE_SYMBOL] = {}
  if (!state[NAMESPACE_SYMBOL][namespace]) state[NAMESPACE_SYMBOL][namespace] = {}
  state[NAMESPACE_SYMBOL][namespace] = {...state[NAMESPACE_SYMBOL][namespace], ...scopedVars}
}

function mutateEnsureNamespaced(scopedStateToMutate: PublishedDict, namespace: string): void {
  if (!(NAMESPACE_SYMBOL in scopedStateToMutate)) scopedStateToMutate[NAMESPACE_SYMBOL] = {}
  const namespaces = scopedStateToMutate[NAMESPACE_SYMBOL]!
  namespaces[namespace] = {}
}
